import React from 'react';
import { Button, Tooltip, CircularProgress } from '@mui/material';
import { Extension, OpenInNew, CheckCircle } from '@mui/icons-material';
import { getChromeStoreUrl } from '../../config/constants';
import { detectBrowser, isChromiumBased, BrowserType } from '../../utils/browserDetection';
import { useExtensionDetection } from '../../hooks/useExtensionDetection';
import { analyticsService } from '../../services/analyticsService';

type LocationType = 'hero' | 'pricing' | 'footer_cta' | 'navbar';

interface InstallExtensionButtonProps {
  location: LocationType;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

const browserMessages: Record<BrowserType, string> = {
  chrome: '',
  edge: '',
  firefox: 'Firefox version coming soon',
  safari: 'Safari is not supported',
  other: 'Please use Chrome or Edge'
};

export const InstallExtensionButton: React.FC<InstallExtensionButtonProps> = ({
  location,
  variant = 'contained',
  size = 'large',
  fullWidth = false,
  color = 'primary'
}) => {
  const { isInstalled, isChecking } = useExtensionDetection();
  const browser = detectBrowser();
  const isCompatible = isChromiumBased();

  const handleClick = () => {
    analyticsService.extensionInstallClick();
    analyticsService.ctaClick('install_extension', location);
  };

  if (isChecking) {
    return (
      <Button variant={variant} size={size} disabled fullWidth={fullWidth}>
        <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
        Checking...
      </Button>
    );
  }

  if (isInstalled) {
    return (
      <Button
        variant={variant}
        size={size}
        color="success"
        fullWidth={fullWidth}
        startIcon={<CheckCircle />}
        disabled
      >
        Extension Installed
      </Button>
    );
  }

  if (!isCompatible) {
    return (
      <Tooltip title={browserMessages[browser]} arrow>
        <span style={{ display: fullWidth ? 'block' : 'inline-block' }}>
          <Button
            variant={variant}
            size={size}
            fullWidth={fullWidth}
            startIcon={<Extension />}
            disabled
            sx={{ pointerEvents: 'none' }}
          >
            Add to Chrome - Free
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      color={color}
      fullWidth={fullWidth}
      startIcon={<Extension />}
      endIcon={<OpenInNew sx={{ fontSize: '0.9em' }} />}
      href={getChromeStoreUrl(location)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
      }}
    >
      Add to Chrome - Free
    </Button>
  );
};

export default InstallExtensionButton;
